import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Loader2, Eye, UserPlus, Download, Mail, RefreshCw,
  TrendingUp, ArrowRight, ExternalLink, Radio,
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, ResponsiveContainer,
  Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import BrandTitle from '../components/BrandTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FOUNDER_EMAIL = 'demo@cartieai.com';

/**
 * AdminConversionFunnel
 * ----------------------
 * Closes the loop on the cornerstone → lead-magnet → email follow-up
 * flywheel. Per-cornerstone funnel: views → subs → downloads → emailed,
 * plus a daily series chart and rate pills.
 *
 * "Leader" cornerstone = best view-to-subscriber rate. Whatever's
 * leading is what topic to write the next post about.
 */

const _STAGE_TONE = {
  view: { c: '#22d3ee', bg: 'bg-emerald-500/15', text: 'text-cyan-200', border: 'border-emerald-400/30' },
  sub: { c: '#34d399', bg: 'bg-emerald-500/15', text: 'text-emerald-200', border: 'border-emerald-400/30' },
  dl: { c: '#22d3ee', bg: 'bg-emerald-500/15', text: 'text-cyan-200', border: 'border-emerald-400/30' },
  email: { c: '#fbbf24', bg: 'bg-amber-500/15', text: 'text-amber-200', border: 'border-amber-400/30' },
  engaged: { c: '#fb7185', bg: 'bg-rose-500/15', text: 'text-rose-200', border: 'border-rose-400/30' },
};

const Stat = ({ icon: Icon, tone, label, value, sub }) => {
  const t = _STAGE_TONE[tone];
  return (
    <div
      className={`rounded-xl border ${t.border} bg-slate-900 text-slate-100 p-4 flex items-start gap-3 min-w-0`}
      data-testid={`funnel-stat-${tone}`}
    >
      <div className={`w-10 h-10 rounded-lg ${t.bg} border ${t.border} flex items-center justify-center shrink-0`}>
        <Icon className={`w-4 h-4 ${t.text}`} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400 mb-0.5">{label}</div>
        <div className="text-2xl font-bold text-white tabular-nums leading-tight">{value?.toLocaleString?.() ?? value ?? 0}</div>
        {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
};

const RatePill = ({ label, rate }) => {
  const display = rate == null ? '—' : `${rate.toFixed(1)}%`;
  let tone = 'text-slate-400 border-slate-700 bg-slate-800';
  if (rate != null) {
    if (rate >= 5) tone = 'text-emerald-200 border-emerald-400/40 bg-emerald-500/15';
    else if (rate >= 2) tone = 'text-amber-200 border-amber-400/40 bg-amber-500/15';
    else tone = 'text-rose-200 border-rose-400/40 bg-rose-500/15';
  }
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[11px] uppercase tracking-widest text-slate-400 font-bold">{label}</span>
      <span className={`px-2 py-0.5 rounded-md border text-[11px] font-bold tabular-nums ${tone}`}>
        {display}
      </span>
    </div>
  );
};

const FunnelRow = ({ funnel, isLeader }) => {
  const s = funnel.stages;
  const r = funnel.rates;
  const slug = funnel.post_slug;
  return (
    <div
      data-testid={`funnel-row-${funnel.slug}`}
      className={`rounded-xl border p-4 transition-colors ${
        isLeader
          ? 'bg-slate-900 border-emerald-400/40'
          : 'bg-slate-900 text-slate-100 border-slate-800 hover:border-slate-700'
      }`}
    >
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-white font-semibold text-sm">{funnel.title}</span>
          {isLeader && (s.subscribers > 0 || s.post_views > 0) && (
            <Badge className="bg-emerald-500/20 text-emerald-200 border-emerald-400/40 text-[11px] tracking-widest">
              LEADER · WRITE NEXT
            </Badge>
          )}
        </div>
        {slug && (
          <a
            href={`/blog/${slug}`}
            target="_blank"
            rel="noreferrer"
            className="text-[11px] text-slate-400 hover:text-slate-400 inline-flex items-center gap-1"
          >
            /blog/{slug}
            <ExternalLink className="w-2.5 h-2.5" />
          </a>
        )}
      </div>
      {/* Stages with arrows */}
      <div className="flex items-center gap-1 flex-wrap text-[12px]">
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-emerald-400/30 bg-emerald-500/10">
          <Eye className="w-3 h-3 text-cyan-300" />
          <span className="font-bold text-cyan-100 tabular-nums">{s.post_views}</span>
          <span className="text-slate-400 text-[11px]">views</span>
        </div>
        <ArrowRight className="w-3 h-3 text-slate-400 mx-0.5" />
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-emerald-400/30 bg-emerald-500/10">
          <UserPlus className="w-3 h-3 text-emerald-300" />
          <span className="font-bold text-emerald-100 tabular-nums">{s.subscribers}</span>
          <span className="text-slate-400 text-[11px]">subs</span>
        </div>
        <ArrowRight className="w-3 h-3 text-slate-400 mx-0.5" />
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-emerald-400/30 bg-emerald-500/10">
          <Download className="w-3 h-3 text-cyan-300" />
          <span className="font-bold text-cyan-100 tabular-nums">{s.downloads}</span>
          <span className="text-slate-400 text-[11px]">dl</span>
        </div>
        <ArrowRight className="w-3 h-3 text-slate-400 mx-0.5" />
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-amber-400/30 bg-amber-500/10">
          <Mail className="w-3 h-3 text-amber-300" />
          <span className="font-bold text-amber-100 tabular-nums">{s.emailed}</span>
          <span className="text-slate-400 text-[11px]">emailed</span>
        </div>
        <ArrowRight className="w-3 h-3 text-slate-400 mx-0.5" />
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-rose-400/30 bg-rose-500/10">
          <Radio className="w-3 h-3 text-rose-300" />
          <span className="font-bold text-rose-100 tabular-nums">{s.engaged ?? 0}</span>
          <span className="text-slate-400 text-[11px]">engaged</span>
        </div>
      </div>
      {/* Rate pills */}
      <div className="flex items-center gap-4 mt-3 flex-wrap">
        <RatePill label="View → Sub" rate={r.view_to_sub_pct} />
        <RatePill label="Sub → DL" rate={r.sub_to_dl_pct} />
        <RatePill label="Sub → Email" rate={r.sub_to_email_pct} />
        <RatePill label="Email → Engaged" rate={r.email_to_engaged_pct} />
      </div>
    </div>
  );
};

const _formatTooltipDate = (label) => {
  try {
    return new Date(label).toLocaleDateString();
  } catch (_e) {
    return label;
  }
};

const AdminConversionFunnel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/conversion-funnel?days=${days}`, {
        headers: { 'X-Founder-Email': FOUNDER_EMAIL },
      });
      setData(r.data);
    } catch (_e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white text-slate-900 px-6 py-12 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-300 mr-2" />
        Loading funnel…
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-white text-slate-900 px-6 py-12">
        <p className="text-center text-rose-300">Could not load funnel data.</p>
      </div>
    );
  }

  const t = data.totals || {};
  const series = data.series || [];
  const leaderSlug = data.funnels?.[0]?.slug;

  return (
    <div
      className="min-h-screen bg-white text-slate-900 px-4 sm:px-6 py-12"
      data-testid="admin-conversion-funnel-page"
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-2"><BrandTitle>Conversion Funnel</BrandTitle></h1>
            <p className="text-sm text-slate-400 max-w-2xl">
              How prospects move from cornerstone post → email subscriber → PDF download → personal email follow-up.
              The cornerstone with the best <b>View → Sub rate</b> is your signal for the next post topic.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                data-testid={`funnel-window-${d}`}
                onClick={() => setDays(d)}
                className={`px-3 py-1.5 rounded-md border text-[11px] font-semibold ${
                  days === d
                    ? 'bg-emerald-500/20 text-emerald-200 border-emerald-400/40'
                    : 'bg-slate-900 text-slate-400 border-slate-800 hover:bg-slate-800'
                }`}
              >
                {d}d
              </button>
            ))}
            <Button
              onClick={load}
              variant="outline"
              size="sm"
              className="bg-slate-900 hover:bg-slate-800 border-slate-700 text-slate-200 ml-1"
              data-testid="funnel-refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Totals — 5 stages */}
        <div
          className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6"
          data-testid="funnel-totals"
        >
          <Stat
            tone="view"
            icon={Eye}
            label="Post Views"
            value={t.post_views}
            sub={`across ${data.funnels?.length || 0} cornerstones`}
          />
          <Stat
            tone="sub"
            icon={UserPlus}
            label="Subscribers"
            value={t.subscribers}
            sub={t.view_to_sub_pct != null ? `${t.view_to_sub_pct}% of views` : '— no views yet'}
          />
          <Stat
            tone="dl"
            icon={Download}
            label="Downloads"
            value={t.downloads}
            sub={t.sub_to_dl_pct != null ? `${t.sub_to_dl_pct}% of subs` : '—'}
          />
          <Stat
            tone="email"
            icon={Mail}
            label="Personal Emails"
            value={t.emailed}
            sub={t.sub_to_email_pct != null ? `${t.sub_to_email_pct}% of subs · go email more` : 'none yet'}
          />
          <Stat
            tone="engaged"
            icon={Radio}
            label="Engaged"
            value={t.engaged}
            sub={t.email_to_engaged_pct != null ? `${t.email_to_engaged_pct}% opened / replied` : 'no opens yet'}
          />
        </div>

        {/* Daily series chart */}
        <Card className="bg-slate-900 text-slate-100 border-slate-800 mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-white flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-300" />
              Daily Trend
              <Badge className="bg-slate-800 text-slate-300 border-slate-700 text-[11px] tracking-wider">
                LAST {data.window_days}D
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={series} margin={{ top: 10, right: 12, bottom: 8, left: 0 }}>
                  <defs>
                    <linearGradient id="g-views" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#22d3ee" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#22d3ee" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="g-subs" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#34d399" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#34d399" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="g-dl" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#22d3ee" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#22d3ee" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="g-engaged" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#fb7185" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#fb7185" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#1e293b" strokeDasharray="2 4" />
                  <XAxis
                    dataKey="date"
                    tick={{ fill: '#64748b', fontSize: 11 }}
                    tickLine={{ stroke: '#334155' }}
                    axisLine={{ stroke: '#334155' }}
                    tickFormatter={(v) => v.slice(5)}
                  />
                  <YAxis
                    tick={{ fill: '#64748b', fontSize: 11 }}
                    tickLine={{ stroke: '#334155' }}
                    axisLine={{ stroke: '#334155' }}
                    width={32}
                  />
                  <Tooltip
                    contentStyle={{
                      background: '#0f172a',
                      border: '1px solid #334155',
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                    labelFormatter={_formatTooltipDate}
                  />
                  <Legend wrapperStyle={{ fontSize: 11, paddingTop: 4 }} iconType="circle" />
                  <Area type="monotone" dataKey="views" name="Views" stroke="#22d3ee" strokeWidth={1.8} fill="url(#g-views)" />
                  <Area type="monotone" dataKey="subs" name="Subscribers" stroke="#34d399" strokeWidth={1.8} fill="url(#g-subs)" />
                  <Area type="monotone" dataKey="downloads" name="Downloads" stroke="#22d3ee" strokeWidth={1.8} fill="url(#g-dl)" />
                  <Area type="monotone" dataKey="engaged" name="Engaged" stroke="#f472b6" strokeWidth={1.8} fill="url(#g-engaged)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Per-magnet funnels */}
        <Card className="bg-slate-900 text-slate-100 border-slate-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-white">Per-Cornerstone Funnel</CardTitle>
            <p className="text-[11px] text-slate-400 mt-0.5">
              Sorted by best View → Sub rate. The top one is your signal — write the next post in a similar topic.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {(data.funnels || []).map((f) => (
              <FunnelRow key={f.slug} funnel={f} isLeader={f.slug === leaderSlug} />
            ))}
          </CardContent>
        </Card>

        <p className="mt-6 text-[11px] text-slate-400 text-center">
          Founder-private · Personal-email count is bumped from <code className="text-slate-400">/admin/leads</code> when you click Email. Engaged = email opened (tracking pixel hit ≥ 1).
        </p>
      </div>
    </div>
  );
};

export default AdminConversionFunnel;
